import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Content, ContentFactoryImpl } from '@mathbuddy/adaptive-practice-engine';
import { applyTheme, Theme } from '../../adaptivePractice/theme';

/**
 * The one way this screen touches the adaptive engine.
 *
 * Three rules hold everywhere a question is loaded, and they are here rather than repeated at each
 * call site:
 *
 *  - ONE AT A TIME. The engine holds a single loaded skill: `loadSkillBySkillId` replaces it and
 *    `displayQuestion` renders out of whatever is loaded now. Two overlapping loads - a teacher
 *    changing skill while the last one is still rendering - would render one skill's question ids
 *    against another skill's content. `run` queues every call.
 *  - NEWEST WINS. A load takes a ticket before it starts and throws its result away if a newer one
 *    has been asked for since, so a slow skill cannot land after a fast one.
 *  - NOTHING AFTER CLOSING. The teacher can close the builder mid-render; `alive` says whether
 *    anything that lands is still wanted.
 */
export const useEngineAccess = (theme: Theme = Theme.Dawn) => {
  const engine = useMemo(() => ({ content: new Content(), factory: new ContentFactoryImpl() }), []);
  useMemo(() => applyTheme(theme), [theme]);

  const queue = useRef<Promise<any>>(Promise.resolve());
  const ticket = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    // Set on mount as well as cleared on unmount: a remount must not leave the screen marked dead.
    alive.current = true;
    return () => {
      alive.current = false;
      ticket.current += 1;
    };
  }, []);

  const run = useCallback(<T,>(job: () => Promise<T>): Promise<T> => {
    const next = queue.current.then(job, job);
    // The queue itself must never stay rejected, or every later call would be dropped.
    queue.current = next.then(
      () => undefined,
      () => undefined,
    );
    return next;
  }, []);

  /** Claim the screen for a new load. The number it returns says which load this is. */
  const claim = useCallback(() => (ticket.current += 1), []);

  /** Is this load still the one the teacher is waiting for? */
  const holds = useCallback((claimed: number) => alive.current && ticket.current === claimed, []);

  /** Abandon whatever is in flight without starting anything. */
  const abandon = useCallback(() => {
    ticket.current += 1;
  }, []);

  return { engine, run, claim, holds, abandon, alive };
};

export default useEngineAccess;
